import React, { SyntheticEvent } from 'react';
import classnames from 'classnames';
import camelCase from 'lodash/camelCase';
import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import Spinner from 'components/spinner/Spinner';
import ScatterChart from 'components/graphs/echarts/scatter-chart/ScatterChartLazy';
import BarGraphMulti from 'components/graphs/echarts/bar-graph-multi/BarGraphMultiLazy';

import ContentPerformanceFilters from './filters/ContentPerformanceFilters';

import { __t, applyTranslations } from 'utils/language';
import { getData as getDataScatterChart } from 'components/graphs/echarts/scatter-chart/utils/scatter-chart';
import { getData as getDataBarGraph } from 'components/graphs/echarts/bar-graph-multi/utils/bar-graph-multi';

import './content-performance.scss';

import type { Data, MixedSelectDropdownItem } from 'types';
import type { ScatterChart as ScatterChartAlias } from 'components/graphs/echarts/scatter-chart/types';
import type { BarGraphMulti as BarGraphMultiAlias } from 'components/graphs/echarts/bar-graph-multi/types';
import type {
  ContentPerformanceDataType,
  ContentPerformanceDropdownOptions,
  ContentPerformanceKpi
} from './types';
import type {
  ReportingMetaInfoIGStorySnippetType,
  ReportingSummaryContentPerformanceGraphBatch,
  ReportingSummaryContentPerformanceGraphDataType,
  ReportingSummaryContentPerformanceGraphItem
} from 'modules/reporting/types';
import type { ConsentReducerConsentsMap } from 'redux-store/types/ConsentReducer.type';

applyTranslations(require('./content-performance.i18n.json'));

type GraphData = ScatterChartAlias | BarGraphMultiAlias;

type Props = {
  contentPerformance: ReportingSummaryContentPerformanceGraphBatch,
  dataTypesToShow: Array<ContentPerformanceDataType>,
  igStorySnippetType: ReportingMetaInfoIGStorySnippetType,
  campaignId: number,
  isLoading: boolean,
  consents: ConsentReducerConsentsMap
};

const DEFAULT_SELECTED_OPTIONS: ContentPerformanceDropdownOptions = {
  kpi: 'impressions',
  format: 'all'
};

const LOCAL_PROP_TO_ENDPOINT_MAP = {
  instagramPost: 'instagramPosts',
  instagramStory: 'instagramStories',
  instagramReel: 'instagramReels',
  tiktokVideo: 'tiktokVideos',
  youtubeVideo: 'youtubeVideos'
};

const MSG_KEY = 'content_performance';

type DataType = Array<ReportingSummaryContentPerformanceGraphItem> | Array<Array<ReportingSummaryContentPerformanceGraphItem>>;

const getPreparedData = (
  contentPerformance: ReportingSummaryContentPerformanceGraphBatch,
  dataType: ReportingSummaryContentPerformanceGraphDataType
): DataType => {
  if (isNil(contentPerformance)) {
    return [];
  }

  if (dataType === 'all') {
    return Object.keys(contentPerformance).map(key => (
      contentPerformance[key]
    ));
  }

  const endpointProp = LOCAL_PROP_TO_ENDPOINT_MAP[camelCase(dataType)];

  return contentPerformance[endpointProp] || [];
};

const getHasDataStatus = (contentPerformance: ReportingSummaryContentPerformanceGraphBatch): boolean => {
  if (isNil(contentPerformance) || isEmpty(contentPerformance)) {
    return false;
  }

  return Object.keys(contentPerformance)
    .some(key => !isEmpty(contentPerformance[key]));
};

const ContentPerformance = ({
  contentPerformance,
  dataTypesToShow,
  igStorySnippetType,
  campaignId,
  consents,
  isLoading
}: Props): JSX.Element => {
  const [view, setView] = React.useState('details');
  const [selectedOptions, setSelectedOptions] = React.useState(DEFAULT_SELECTED_OPTIONS);

  const handleSetView = (event: SyntheticEvent<HTMLElement>): void => {
    const target = event.currentTarget;
    const currentView = target.dataset.arg || '';

    setView(currentView);
  };

  const handleSetSelectedOptions = (
    items: Array<MixedSelectDropdownItem>,
    data?: Data | null
  ): void => {
    const foundItem = items.find(item => item.isSelected);
    const type = data?.type;

    if (type && foundItem) {
      setSelectedOptions({
        ...selectedOptions,
        [type]: foundItem.identifier
      });
    }
  };

  const renderSpinner = (): JSX.Element => (
    <div className="content-performance__spinner">
      <Spinner
        position="inline"
        size="small"
        color="light"
      />
    </div>
  );

  const renderEmpty = (): JSX.Element => (
    <div className="content-performance__empty">
      {__t(`${MSG_KEY}.messages.no_content`)}
    </div>
  );

  const renderScatterChart = (
    graphData: ScatterChartAlias
  ): JSX.Element => (
    <ScatterChart
      key={`content-performance--scatter-${selectedOptions.kpi}-${selectedOptions.format}`}
      data={graphData}
    />
  );

  const renderBarGraph = (
    graphData: BarGraphMultiAlias
  ): JSX.Element => (
    <BarGraphMulti
      key={`content-performance--bar-${selectedOptions.kpi}-${selectedOptions.format}`}
      data={graphData}
    />
  );

  const renderGraph = (
    graphData: Partial<GraphData>,
    noData: boolean
  ): JSX.Element => {
    if (isLoading) {
      return renderSpinner();
    }

    if (noData) {
      return renderEmpty();
    }

    return view === 'details'
      ? renderScatterChart(graphData as ScatterChartAlias)
      : renderBarGraph(graphData as BarGraphMultiAlias);
  };

  const kpi: ContentPerformanceKpi = (camelCase(selectedOptions.kpi) as ContentPerformanceKpi);
  const preparedData = getPreparedData(contentPerformance, selectedOptions.format);
  const noData = isEmpty(preparedData) || !getHasDataStatus(contentPerformance);
  const scatterChartData = getDataScatterChart(flatten(preparedData), kpi, consents, campaignId, igStorySnippetType);
  const barGraphData = getDataBarGraph(flatten(preparedData), kpi);

  const graphClasses = classnames('content-performance__graph', {
    'content-performance__graph--is-empty': noData
  });

  const graphData = view === 'details'
    ? scatterChartData
    : barGraphData;

  return (
    <div className="content-performance">
      <div className="content-performance__title">
        {__t(`${MSG_KEY}.title`)}
      </div>
      <ContentPerformanceFilters
        selectedOptions={selectedOptions}
        dataTypesToShow={dataTypesToShow}
        view={view}
        onSetSelectedOptions={handleSetSelectedOptions}
        onSetView={handleSetView}
      />
      {
        (!noData || isLoading) && (
          <div className="content-performance__instruction">
            {__t(`${MSG_KEY}.messages.use_trackpad`)}
          </div>
        )
      }
      <div className={graphClasses}>
        {renderGraph(graphData, noData)}
      </div>
    </div>
  );
};

export default ContentPerformance;
